/**
 * Intake Form Template Service
 *
 * Client wrapper for the form-builder CRUD API.
 *   GET    /api/clinics/:clinicId/intake-form-templates
 *   POST   /api/clinics/:clinicId/intake-form-templates
 *   GET    /api/clinics/:clinicId/intake-form-templates/:id
 *   PUT    /api/clinics/:clinicId/intake-form-templates/:id
 *   DELETE /api/clinics/:clinicId/intake-form-templates/:id  (soft archive)
 *   POST   /api/clinics/:clinicId/intake-form-templates/:id/duplicate
 */

import { api } from './api';
import type { IntakeSection } from '@/lib/intake/templates/types';

export interface IntakeFormTemplateSchema {
  sections: IntakeSection[];
}

export interface IntakeFormTemplate {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  schema: IntakeFormTemplateSchema;
  version: number;
  is_active: boolean;
  is_published: boolean;
  estimated_minutes: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntakeFormTemplateCreate {
  name: string;
  description?: string;
  schema: IntakeFormTemplateSchema;
  estimated_minutes?: number;
  is_published?: boolean;
}

export type IntakeFormTemplateUpdate = Partial<IntakeFormTemplateCreate> & {
  is_active?: boolean;
};

interface ListResponse {
  success: boolean;
  data: IntakeFormTemplate[];
}

interface ItemResponse {
  success: boolean;
  data: IntakeFormTemplate;
}

const base = (clinicId: string) => `/clinics/${clinicId}/intake-form-templates`;

export const intakeFormTemplateService = {
  async list(
    clinicId: string,
    opts: { active?: boolean; published?: boolean } = {},
  ): Promise<IntakeFormTemplate[]> {
    const params = new URLSearchParams();
    if (opts.active !== undefined) params.set('active', String(opts.active));
    if (opts.published !== undefined) params.set('published', String(opts.published));
    const qs = params.toString();
    const res = await api.get<ListResponse>(`${base(clinicId)}${qs ? `?${qs}` : ''}`);
    return res.data || [];
  },

  async get(clinicId: string, id: string): Promise<IntakeFormTemplate | null> {
    try {
      const res = await api.get<ItemResponse>(`${base(clinicId)}/${id}`);
      return res.data || null;
    } catch (err: any) {
      if (err?.status === 404 || err?.response?.status === 404) return null;
      throw err;
    }
  },

  async create(clinicId: string, payload: IntakeFormTemplateCreate): Promise<IntakeFormTemplate> {
    const res = await api.post<ItemResponse>(base(clinicId), payload);
    return res.data;
  },

  async update(
    clinicId: string,
    id: string,
    payload: IntakeFormTemplateUpdate,
  ): Promise<IntakeFormTemplate> {
    const res = await api.put<ItemResponse>(`${base(clinicId)}/${id}`, payload);
    return res.data;
  },

  async archive(clinicId: string, id: string): Promise<void> {
    await api.delete(`${base(clinicId)}/${id}`);
  },

  async duplicate(clinicId: string, id: string): Promise<IntakeFormTemplate> {
    const res = await api.post<ItemResponse>(`${base(clinicId)}/${id}/duplicate`, {});
    return res.data;
  },
};

export default intakeFormTemplateService;
